import React from 'react';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { ThemeProvider } from 'styled-components';
import { lightTheme } from '@themes/lightTheme';
import GlobalStyle from '@themes/GlobalStyle';
import ObservabilityErrorBoundary from '@utils/services/observability/ObservabilityErrorBoundary';
import { LocaleProvider } from '@utils/locale/locale';
import { TestableProvider } from './utils/testable/testable';
import { SessionProvider } from '@utils/context/session-provider';
import { AppRouter } from './router';
import { ErrorBoundary } from '@components/ErrorBoundary';
import { initialiseDataDog } from '@utils/services/datadog/datadog-helpers';
import { setupDynatrace } from '@utils/services/dynatrace/dynatrace-setup';
import { LocalStorageKeys } from 'utils/localStorage';
import { AppLogger, getCorrelationId } from '@workivate/tho-web-shared';

// Initialize DataDog if enabled
if (import.meta.env.DD_APP_ID && import.meta.env.DD_CLIENT_TOKEN) {
  initialiseDataDog();
}

const dynatraceEnabled =
  localStorage.getItem(LocalStorageKeys.dynatraceEnabled) !== 'false';

// Capture the correlation ID from URL params synchronously before the async Dynatrace
// IIFE starts. DynatraceClient.init() calls getCorrelationId() after the script loads,
// by which time the URL may have been modified. Storing it in localStorage here ensures
// DynatraceClient.init() reads the correct value.
getCorrelationId({
  urlSearchParams: new URLSearchParams(window.location.search),
});

if (dynatraceEnabled) {
  const scriptUrl = import.meta.env.DYNATRACE_SCRIPT_URL;

  if (!scriptUrl) {
    AppLogger.logWithOptions({
      message: '[DT] DYNATRACE_SCRIPT_URL is not set; skipping Dynatrace init',
    });
  } else {
    void setupDynatrace(scriptUrl).catch((e) => {
      AppLogger.logWithOptions({
        message: '[DT] Failed to load Dynatrace script',
        error: e,
      });
    });
  }
}

export const App: React.FC = () => {
  // At present USL has only light theme. If we ever add dark theme support,
  // we can make this dynamic based on user preference or system settings.
  const theme = lightTheme;

  // SessionProvider must be ancestor of LocaleProvider not descendant
  // to avoid reinitialising session context on locale change
  return (
    <HelmetProvider>
      <ObservabilityErrorBoundary fallback={<div>An error occurred</div>}>
        <ThemeProvider theme={theme}>
          <BrowserRouter>
            <TestableProvider>
              <SessionProvider>
                <LocaleProvider>
                  <GlobalStyle />
                  <ErrorBoundary>
                    <AppRouter />
                  </ErrorBoundary>
                </LocaleProvider>
              </SessionProvider>
            </TestableProvider>
          </BrowserRouter>
        </ThemeProvider>
      </ObservabilityErrorBoundary>
    </HelmetProvider>
  );
};
